import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ClipboardCheck, RefreshCw, Video, Image as ImageIcon, MessageSquareText, Dumbbell, FileDown, Mail, Pencil, FileUp, Zap, Target } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Modal, Field, inputCls, labelCls, SubmitBtn } from "./shared";

const TEST_FIELDS = [
  ["ten_yard", "10-Yard (s)"], ["twenty_yard", "20-Yard (s)"], ["forty_yard", "40-Yard (s)"],
  ["flying_ten", "Flying 10 (s)"], ["flying_twenty", "Flying 20 (s)"], ["thirty_m", "30m (s)"],
  ["sixty_m", "60m (s)"], ["hundred_m", "100m (s)"], ["vertical", "Vertical (in)"],
  ["broad", "Broad (in)"], ["height", "Height (in)"], ["weight", "Weight (lb)"],
  ["body_fat", "Body Fat (%)"], ["mechanics_score", "Mechanics (0-100)"],
];

const ACTIONS = [
  { key: "evaluation", label: "Add Evaluation", icon: ClipboardCheck },
  { key: "retest", label: "Add Retest", icon: RefreshCw },
  { key: "video", label: "Upload Video", icon: Video },
  { key: "photo", label: "Upload Photo", icon: ImageIcon },
  { key: "feedback", label: "Add Feedback", icon: MessageSquareText },
  { key: "program", label: "Assign Program", icon: Dumbbell },
  { key: "goal", label: "Assign Goal", icon: Target },
  { key: "sp", label: "Award SP", icon: Zap },
  { key: "document", label: "Upload Document", icon: FileUp },
  { key: "report", label: "Generate Report", icon: FileDown },
  { key: "message", label: "Message Parent", icon: Mail },
  { key: "edit", label: "Edit Info", icon: Pencil },
];

export default function QuickActions({ athlete, onRefresh }) {
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const id = athlete.id;

  const open = (key) => {
    setForm(key === "edit" ? {
      name: athlete.name || "", sport: athlete.sport || "", position: athlete.position || "", age: athlete.age || "",
      grad_year: athlete.grad_year || "", school: athlete.school || "", height: athlete.height || "", weight: athlete.weight || "",
      training_phase: athlete.training_phase || "", next_retest_date: athlete.next_retest_date ? athlete.next_retest_date.slice(0, 10) : "",
      status: athlete.status || "active", goals: athlete.goals || "", injuries: athlete.injuries || "", notes: athlete.notes || "",
    } : key === "video" || key === "photo" ? { kind: "sprint", category: "training" }
      : key === "program" ? { week: 1 } : {});
    setFile(null);
    setActive(key);
  };
  const close = () => { setActive(null); setBusy(false); };
  const done = (msg) => { toast.success(msg); close(); onRefresh(); };
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const num = (v) => (v === "" || v == null ? null : parseFloat(v));

  const submitTest = async (record_type) => {
    setBusy(true);
    try {
      const payload = { athlete_id: id, record_type, date: form.date || undefined, is_baseline: !!form.is_baseline, notes: form.notes || undefined };
      TEST_FIELDS.forEach(([k]) => { const n = num(form[k]); if (n != null) payload[k] = n; });
      await api.post("/test-records", payload);
      done(record_type === "retest" ? "Retest recorded." : "Evaluation recorded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitMedia = async (media_type) => {
    if (!file) { toast.error("Choose a file first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const q = `athlete_id=${id}&kind=${encodeURIComponent(form.kind || "general")}&media_type=${media_type}&category=${encodeURIComponent(form.category || "")}`;
      await api.post(`/videos?${q}`, fd);
      done(media_type === "photo" ? "Photo uploaded." : "Video uploaded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitFeedback = async () => {
    setBusy(true);
    try {
      await api.post("/feedback", { athlete_id: id, session_date: form.session_date || undefined, focus: form.focus || undefined, strengths: form.strengths || undefined, improvements: form.improvements || undefined, homework: form.homework || undefined, next_focus: form.next_focus || undefined });
      done("Feedback added.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitProgram = async () => {
    setBusy(true);
    try {
      await api.post("/programs", { athlete_id: id, title: form.title, week: parseInt(form.week, 10) || 1, instructions: form.instructions || undefined, homework: form.homework || undefined, items: (form.items || "").split("\n").map((s) => s.trim()).filter(Boolean) });
      done("Program assigned.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitGoal = async () => {
    setBusy(true);
    try {
      await api.post("/goals", { athlete_id: id, title: form.title, description: form.description || undefined, target_date: form.target_date || undefined });
      done("Goal assigned.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitSP = async () => {
    setBusy(true);
    try {
      await api.post(`/athletes/${id}/speed-points`, { points: parseInt(form.points, 10) || 0, reason: form.reason, category: "coach_award" });
      done("Speed Points awarded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitDocument = async () => {
    if (!file) { toast.error("Choose a file first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const q = `athlete_id=${id}&name=${encodeURIComponent(form.name || file.name)}&category=${encodeURIComponent(form.category || "")}`;
      await api.post(`/documents?${q}`, fd);
      done("Document uploaded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitReport = async () => {
    setBusy(true);
    try { await api.post(`/athletes/${id}/progress-report`); done("Report generated — see Documents tab."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitMessage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/athletes/${id}/message-parent`, { subject: form.subject, message: form.message });
      done(data.parents ? `Message sent to ${data.parents} parent(s).` : "No linked parents to message.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const submitEdit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.patch(`/athletes/${id}`, payload);
      done("Athlete info updated.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const TestForm = ({ type }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Date"><input data-testid="qa-test-date" type="date" value={form.date || ""} onChange={(e) => upd({ date: e.target.value })} className={inputCls} /></Field>
        <label className="flex items-end gap-2 pb-2 text-sm text-white/70"><input data-testid="qa-test-baseline" type="checkbox" checked={!!form.is_baseline} onChange={(e) => upd({ is_baseline: e.target.checked })} className="accent-white" /> Mark as baseline</label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {TEST_FIELDS.map(([k, lbl]) => (
          <Field key={k} label={lbl}><input data-testid={`qa-test-${k}`} type="number" step="0.01" value={form[k] || ""} onChange={(e) => upd({ [k]: e.target.value })} className={inputCls} /></Field>
        ))}
      </div>
      <Field label="Notes"><textarea data-testid="qa-test-notes" rows={2} value={form.notes || ""} onChange={(e) => upd({ notes: e.target.value })} className={inputCls} /></Field>
      <SubmitBtn data-testid="qa-test-submit" disabled={busy} onClick={() => submitTest(type)}>{busy ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />} Save {type === "retest" ? "Retest" : "Evaluation"}</SubmitBtn>
    </div>
  );

  const MediaForm = ({ type }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Kind"><select data-testid="qa-media-kind" value={form.kind} onChange={(e) => upd({ kind: e.target.value })} className={inputCls}>{["sprint", "broad", "vertical", "general"].map((k) => <option key={k} value={k}>{k}</option>)}</select></Field>
        <Field label="Category"><select data-testid="qa-media-category" value={form.category} onChange={(e) => upd({ category: e.target.value })} className={inputCls}>{["training", "evaluation", "retest", "camp"].map((k) => <option key={k} value={k}>{k}</option>)}</select></Field>
      </div>
      <Field label={type === "photo" ? "Photo file" : "Video file"}><input data-testid="qa-media-file" type="file" accept={type === "photo" ? "image/*" : "video/*"} onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm text-white/70 file:mr-3 file:border-0 file:bg-white file:text-black file:px-4 file:py-2 file:font-bold file:uppercase file:text-xs file:tracking-widest" /></Field>
      <SubmitBtn data-testid="qa-media-submit" disabled={busy} onClick={() => submitMedia(type)}>{busy ? <Loader2 size={14} className="animate-spin" /> : (type === "photo" ? <ImageIcon size={14} /> : <Video size={14} />)} Upload</SubmitBtn>
    </div>
  );

  return (
    <>
      <div className="bg-[#121212] border border-white/10 p-4 lg:sticky lg:top-6" data-testid="quick-actions-panel">
        <p className="flex items-center gap-2 font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4"><Zap size={12} /> Quick Actions</p>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <button key={a.key} data-testid={`qa-${a.key}`} onClick={() => open(a.key)} className="flex items-center gap-2.5 border border-white/15 px-3 py-2.5 text-xs uppercase tracking-widest font-bold text-white/70 hover:bg-white hover:text-black transition-colors text-left">
                <Icon size={14} className="shrink-0" /> <span className="truncate">{a.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Modal open={active === "evaluation"} onClose={close} title="Add Evaluation" testid="qa-modal-evaluation" wide><TestForm type="evaluation" /></Modal>
      <Modal open={active === "retest"} onClose={close} title="Add Retest" testid="qa-modal-retest" wide><TestForm type="retest" /></Modal>
      <Modal open={active === "video"} onClose={close} title="Upload Video" testid="qa-modal-video"><MediaForm type="video" /></Modal>
      <Modal open={active === "photo"} onClose={close} title="Upload Photo" testid="qa-modal-photo"><MediaForm type="photo" /></Modal>

      <Modal open={active === "feedback"} onClose={close} title="Add Coach Feedback" testid="qa-modal-feedback" wide>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Session Date"><input data-testid="qa-fb-date" type="date" value={form.session_date || ""} onChange={(e) => upd({ session_date: e.target.value })} className={inputCls} /></Field>
            <Field label="Focus"><input data-testid="qa-fb-focus" value={form.focus || ""} onChange={(e) => upd({ focus: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Strengths"><textarea data-testid="qa-fb-strengths" rows={2} value={form.strengths || ""} onChange={(e) => upd({ strengths: e.target.value })} className={inputCls} /></Field>
          <Field label="Areas to Improve"><textarea data-testid="qa-fb-improvements" rows={2} value={form.improvements || ""} onChange={(e) => upd({ improvements: e.target.value })} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Homework"><input data-testid="qa-fb-homework" value={form.homework || ""} onChange={(e) => upd({ homework: e.target.value })} className={inputCls} /></Field>
            <Field label="Next Focus"><input data-testid="qa-fb-next" value={form.next_focus || ""} onChange={(e) => upd({ next_focus: e.target.value })} className={inputCls} /></Field>
          </div>
          <SubmitBtn data-testid="qa-fb-submit" disabled={busy} onClick={submitFeedback}>{busy ? <Loader2 size={14} className="animate-spin" /> : <MessageSquareText size={14} />} Save Feedback</SubmitBtn>
        </div>
      </Modal>

      <Modal open={active === "program"} onClose={close} title="Assign Program" testid="qa-modal-program" wide>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><Field label="Title"><input data-testid="qa-prog-title" value={form.title || ""} onChange={(e) => upd({ title: e.target.value })} className={inputCls} /></Field></div>
            <Field label="Week"><input data-testid="qa-prog-week" type="number" min="1" value={form.week || 1} onChange={(e) => upd({ week: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Instructions"><textarea data-testid="qa-prog-instructions" rows={2} value={form.instructions || ""} onChange={(e) => upd({ instructions: e.target.value })} className={inputCls} /></Field>
          <Field label="Items (one per line)"><textarea data-testid="qa-prog-items" rows={4} value={form.items || ""} onChange={(e) => upd({ items: e.target.value })} className={inputCls} placeholder={"Sprint drills 3x30m\nSquat 4x5\nMobility 10min"} /></Field>
          <Field label="Homework"><input data-testid="qa-prog-homework" value={form.homework || ""} onChange={(e) => upd({ homework: e.target.value })} className={inputCls} /></Field>
          <SubmitBtn data-testid="qa-prog-submit" disabled={busy || !form.title} onClick={submitProgram}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Dumbbell size={14} />} Assign</SubmitBtn>
        </div>
      </Modal>

      <Modal open={active === "goal"} onClose={close} title="Assign Goal" testid="qa-modal-goal" wide>
        <div className="space-y-4">
          <Field label="Goal Title"><input data-testid="qa-goal-title" value={form.title || ""} onChange={(e) => upd({ title: e.target.value })} className={inputCls} /></Field>
          <Field label="Description"><textarea data-testid="qa-goal-desc" rows={3} value={form.description || ""} onChange={(e) => upd({ description: e.target.value })} className={inputCls} /></Field>
          <Field label="Target Date"><input data-testid="qa-goal-date" type="date" value={form.target_date || ""} onChange={(e) => upd({ target_date: e.target.value })} className={inputCls} /></Field>
          <SubmitBtn data-testid="qa-goal-submit" disabled={busy || !form.title} onClick={submitGoal}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Target size={14} />} Assign Goal</SubmitBtn>
        </div>
      </Modal>

      <Modal open={active === "sp"} onClose={close} title="Award Speed Points" testid="qa-modal-sp">
        <div className="space-y-4">
          <Field label="Points"><input data-testid="qa-sp-points" type="number" value={form.points || ""} onChange={(e) => upd({ points: e.target.value })} className={inputCls} /></Field>
          <Field label="Reason"><input data-testid="qa-sp-reason" value={form.reason || ""} onChange={(e) => upd({ reason: e.target.value })} placeholder="Camp attendance, Coach award…" className={inputCls} /></Field>
          <SubmitBtn data-testid="qa-sp-submit" disabled={busy || !form.points || !form.reason} onClick={submitSP}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Award SP</SubmitBtn>
        </div>
      </Modal>

      <Modal open={active === "document"} onClose={close} title="Upload Document" testid="qa-modal-document">
        <div className="space-y-4">
          <Field label="Name"><input data-testid="qa-doc-name" value={form.name || ""} onChange={(e) => upd({ name: e.target.value })} placeholder="Waiver, Report, ID…" className={inputCls} /></Field>
          <Field label="Category"><input data-testid="qa-doc-category" value={form.category || ""} onChange={(e) => upd({ category: e.target.value })} placeholder="Waiver / Medical / Report" className={inputCls} /></Field>
          <Field label="File"><input data-testid="qa-doc-file" type="file" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm text-white/70 file:mr-3 file:border-0 file:bg-white file:text-black file:px-4 file:py-2 file:font-bold file:uppercase file:text-xs file:tracking-widest" /></Field>
          <SubmitBtn data-testid="qa-doc-submit" disabled={busy} onClick={submitDocument}>{busy ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Upload</SubmitBtn>
        </div>
      </Modal>

      <Modal open={active === "report"} onClose={close} title="Generate Progress Report" testid="qa-modal-report">
        <p className="text-white/60 text-sm mb-5">Generate a PDF progress report from {athlete.name}'s current snapshot and testing history. It will be saved to the Documents tab.</p>
        <SubmitBtn data-testid="qa-report-submit" disabled={busy} onClick={submitReport}>{busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Generate PDF</SubmitBtn>
      </Modal>

      <Modal open={active === "message"} onClose={close} title="Message Parent" testid="qa-modal-message" wide>
        <div className="space-y-4">
          <Field label="Subject"><input data-testid="qa-msg-subject" value={form.subject || ""} onChange={(e) => upd({ subject: e.target.value })} className={inputCls} /></Field>
          <Field label="Message"><textarea data-testid="qa-msg-message" rows={5} value={form.message || ""} onChange={(e) => upd({ message: e.target.value })} className={inputCls} /></Field>
          <SubmitBtn data-testid="qa-msg-submit" disabled={busy || !form.subject || !form.message} onClick={submitMessage}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send</SubmitBtn>
        </div>
      </Modal>

      <Modal open={active === "edit"} onClose={close} title="Edit Athlete Info" testid="qa-modal-edit" wide>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[["name", "Name"], ["sport", "Sport"], ["position", "Position"], ["age", "Age"], ["grad_year", "Grad Year"], ["school", "School"], ["height", "Height"], ["weight", "Weight"], ["training_phase", "Training Phase"]].map(([k, lbl]) => (
              <Field key={k} label={lbl}><input data-testid={`qa-edit-${k}`} value={form[k] || ""} onChange={(e) => upd({ [k]: e.target.value })} className={inputCls} /></Field>
            ))}
            <Field label="Next Retest Date"><input data-testid="qa-edit-retest" type="date" value={form.next_retest_date || ""} onChange={(e) => upd({ next_retest_date: e.target.value })} className={inputCls} /></Field>
            <Field label="Status"><select data-testid="qa-edit-status" value={form.status || "active"} onChange={(e) => upd({ status: e.target.value })} className={inputCls}>{["active", "inactive", "pending"].map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          </div>
          <Field label="Goals"><textarea data-testid="qa-edit-goals" rows={2} value={form.goals || ""} onChange={(e) => upd({ goals: e.target.value })} className={inputCls} /></Field>
          <Field label="Injuries"><textarea data-testid="qa-edit-injuries" rows={2} value={form.injuries || ""} onChange={(e) => upd({ injuries: e.target.value })} className={inputCls} /></Field>
          <Field label="Notes"><textarea data-testid="qa-edit-notes" rows={2} value={form.notes || ""} onChange={(e) => upd({ notes: e.target.value })} className={inputCls} /></Field>
          <SubmitBtn data-testid="qa-edit-submit" disabled={busy} onClick={submitEdit}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} Save Changes</SubmitBtn>
        </div>
      </Modal>
    </>
  );
}
